import { createFileRoute } from "@tanstack/react-router";
import { ogImageMeta } from "@/lib/seo/og-image";
import { HelpCircle } from "lucide-react";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { MediaMonitoringSubNav } from "@/components/site/MediaMonitoringSubNav";
import { FaqSearch } from "@/components/site/FaqSearch";
import { RequestDemoInlineForm } from "@/components/site/RequestDemoInlineForm";
import { platformFaqsBg } from "@/content/faqs";

import { pageSchemas } from "@/lib/seo/page-schema";
const META_DESCRIPTION =
  "Често задавани въпроси за платформата за медиен мониторинг на Perceptica - цени, източници, езици, експорт и въвеждане в експлоатация.";

export const Route = createFileRoute("/bg/media-monitoring/platform_/faq")({
  head: () => ({
    meta: [
      { title: "Платформа за медиен мониторинг - често задавани въпроси | Perceptica" },
      { name: "description", content: META_DESCRIPTION },
      { property: "og:title", content: "Платформа за медиен мониторинг - често задавани въпроси | Perceptica" },
      { property: "og:description", content: META_DESCRIPTION },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Платформа за медиен мониторинг - често задавани въпроси | Perceptica" },
      { name: "twitter:description", content: META_DESCRIPTION },
      { property: "og:url", content: "https://perceptica.com/bg/media-monitoring/platform/faq" },
      ...ogImageMeta("bg", "/media-monitoring/platform/faq"),
    ],
  
    scripts: pageSchemas({ locale: "bg", path: "/media-monitoring/platform/faq", faq: platformFaqsBg }),
  }),
  component: PlatformFaqPageBg,
});

function PlatformFaqPageBg() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main id="main">
        <section className="relative overflow-hidden bg-primary text-primary-foreground">
          <div className="grid-overlay" />
          <div
            aria-hidden
            className="pointer-events-none absolute -bottom-24 right-0 h-[420px] w-[420px] rounded-full opacity-30 blur-[140px]"
            style={{ background: "radial-gradient(circle, #FF2D78 0%, transparent 70%)" }}
          />
          <div className="noise-overlay" />
          <div className="relative mx-auto max-w-[1100px] px-6 py-20 lg:py-28">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/75">
              <HelpCircle className="h-3.5 w-3.5" /> Често задавани въпроси за платформата
            </span>
            <h1 className="mt-5 max-w-[28ch] font-display text-[clamp(2.25rem,4.4vw,3.25rem)] font-medium leading-[1.05] text-white">
              Всичко, което може да ви интересува за MLP 2.0
            </h1>
            <p className="mt-6 max-w-2xl text-[15.5px] leading-relaxed text-white/70">
              Покритие, тоналност, сигнали, експорт и достъп - най-честите въпроси, които екипът ни получава за Perceptica Media Listening Platform.
            </p>
          </div>
        </section>

        <MediaMonitoringSubNav />

        <section className="py-20 lg:py-24">
          <div className="mx-auto max-w-[900px] px-6 lg:px-10">
            <FaqSearch items={platformFaqsBg} idPrefix="platform-faq-bg" placeholder="Търсене във въпросите..." />
          </div>
        </section>

        <section className="bg-primary text-primary-foreground">
          <div className="mx-auto grid max-w-[1100px] items-center gap-12 px-6 py-20 lg:grid-cols-[1fr_1.1fr] lg:py-24">
            <div className="text-center lg:text-left">
              <h2 className="font-display max-w-[26ch] text-[clamp(1.75rem,3vw,2.5rem)] text-white max-lg:mx-auto">
                Все още имате въпроси за платформата?
              </h2>
              <p className="mt-4 max-w-xl text-[15px] leading-relaxed text-white/70 max-lg:mx-auto">
                Заявете персонализирано демо и нашият екип ще ви преведе през MLP 2.0 с вашата организация, конкурентите и темите ви, вече заредени в системата.
              </p>
              <p className="mt-8 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/60">
                На повечето запитвания отговаряме в рамките на един работен ден.
              </p>
            </div>
            <RequestDemoInlineForm formName="request-a-demo-platform-faq-bg" locale="bg" />
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
